import axios from 'axios';

// Cấu hình API base URL
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:5000/api';

// Tạo instance axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];
let onTokenRefresh = null; // Callback for token updates

// Set token refresh callback (called by AuthContext)
export const setTokenRefreshCallback = (callback) => {
  onTokenRefresh = callback;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Interceptor để thêm token vào header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor để xử lý response và refresh token tự động
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        try {
          // Use direct axios call to avoid interceptor loop
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${refreshToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 10000 // Add timeout for refresh requests
            }
          );

          const { access_token } = response.data;
          
          if (!access_token) {
            throw new Error('No access token in refresh response');
          }

          // Store the new access token
          localStorage.setItem('token', access_token);
          
          // Update default headers for future requests
          api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          
          // Notify AuthContext about token update
          if (onTokenRefresh) {
            onTokenRefresh(access_token);
          }
          
          // Process queued requests with new token
          processQueue(null, access_token);
          isRefreshing = false;
          
          // Retry the original request
          return api(originalRequest);
          
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          processQueue(refreshError, null);
          isRefreshing = false;
          
          // Clear all auth data
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          
          // Only redirect if we're not already on the login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available, clear everything and redirect
        console.warn('No refresh token available for token refresh');
        isRefreshing = false;
        
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  refresh: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return Promise.reject(new Error('No refresh token available'));
    }
    
    // Use direct axios call to avoid interceptor loop
    return axios.post(
      `${API_BASE_URL}/auth/refresh`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${refreshToken}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000 // Add timeout
      }
    ).then(response => {
      const { access_token } = response.data;
      
      if (!access_token) {
        throw new Error('No access token in refresh response');
      }
      
      // Update stored token
      localStorage.setItem('token', access_token);
      
      // Update default headers
      api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
      
      // Notify AuthContext about token update
      if (onTokenRefresh) {
        onTokenRefresh(access_token);
      }
      
      return response;
    }).catch(error => {
      console.error('Manual token refresh failed:', error);
      
      // Clear tokens on refresh failure
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      throw error;
    });
  },
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (userData) => api.put('/auth/profile', userData),
  resetPassword: (userId, newPassword) => 
    api.post('/auth/admin/reset-password', { user_id: userId, new_password: newPassword }),
};

// Music API
export const musicAPI = {
  list: (params = {}) => api.get('/music/list', { params }),
  upload: (formData) => api.post('/music/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  get: (id) => api.get(`/music/${id}`),
  delete: (id) => api.delete(`/music/${id}`),
  stream: (id) => `${API_BASE_URL}/music/${id}/stream`,
  
  // User dashboard
  getDashboard: () => api.get('/music/dashboard'),
  
  // User playback control
  controlPlayback: (action, data = {}) => 
    api.post('/music/playback/control', { action, ...data }),
  getPlaybackStatus: () => api.get('/music/playback/status'),
  
  // Playlist navigation
  nextTrack: () => api.post('/music/playback/next'),
  previousTrack: () => api.post('/music/playback/previous'),
  
  // Playlist endpoints
  getPlaylists: () => api.get('/music/playlists'),
  createPlaylist: (playlistData) => api.post('/music/playlists', playlistData),
  getPlaylist: (id) => api.get(`/music/playlists/${id}`),
  updatePlaylist: (id, playlistData) => api.put(`/music/playlists/${id}`, playlistData),
  deletePlaylist: (id) => api.delete(`/music/playlists/${id}`),
  addToPlaylist: (playlistId, musicId) => 
    api.post(`/music/playlists/${playlistId}/add`, { music_id: musicId }),
  removeFromPlaylist: (playlistId, musicId) => 
    api.delete(`/music/playlists/${playlistId}/remove/${musicId}`),
  batchUpdatePlaylist: (playlistId, { add_music_ids = [], remove_music_ids = [] }) =>
    api.post(`/music/playlists/${playlistId}/batch`, { add_music_ids, remove_music_ids }),
  reorderPlaylist: (playlistId, musicOrder) => 
    api.put(`/music/playlists/${playlistId}/reorder`, { music_order: musicOrder }),
  getRandomTrack: (playlistId) => api.get(`/music/playlists/${playlistId}/random`),
  
  // New optimized endpoint for available music
  getAvailableForPlaylist: (playlistId, params = {}) => 
    api.get(`/music/available-for-playlist/${playlistId}`, { params }),
};

// Schedule API
export const scheduleAPI = {
  list: () => api.get('/schedule/list'),
  create: (scheduleData) => api.post('/schedule/create', scheduleData),
  get: (id) => api.get(`/schedule/${id}`),
  update: (id, scheduleData) => api.put(`/schedule/${id}`, scheduleData),
  delete: (id) => api.delete(`/schedule/${id}`),
  getCurrent: () => api.get('/schedule/current'),
  
  // User schedule operations
  createUserSchedule: (scheduleData) => api.post('/schedule/create', {
    ...scheduleData,
    priority: Math.min(Math.max(scheduleData.priority || 1, 1), 10) // Ensure user priority is 1-10
  }),
  
  // Admin schedule operations
  createAdminSchedule: (scheduleData) => api.post('/schedule/create', {
    ...scheduleData,
    priority: scheduleData.priority || 100 // Default admin priority
  }),
  
  // Get schedules by type
  getUserSchedules: () => api.get('/schedule/list').then(response => {
    // Filter user schedules on client side if needed
    return response;
  }),
  
  // Toggle schedule active status
  toggleSchedule: (id, isActive) => api.put(`/schedule/${id}`, { is_active: isActive }),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  
  // Playback control
  controlPlayback: (action, data = {}) => 
    api.post('/admin/playback/control', { action, ...data }),
  getPlaybackStatus: () => api.get('/admin/playback/status'),
  overridePlayback: (data) => api.post('/admin/override', data),
  stopOverride: () => api.post('/admin/override/stop'),
  
  // Playlist navigation
  nextTrack: () => api.post('/admin/playback/next'),
  previousTrack: () => api.post('/admin/playback/previous'),
  
  // System status
  getSystemStatus: () => api.get('/admin/system/status'),
};

// Device API
export const deviceAPI = {
  // Device management
  list: () => api.get('/device/list'),
  register: (deviceData) => api.post('/device/register', deviceData),
  get: (id) => api.get(`/device/${id}`),
  update: (id, deviceData) => api.put(`/device/${id}`, deviceData),
  delete: (id) => api.delete(`/device/${id}`),
  regenerateToken: (id) => api.post(`/device/${id}/regenerate-token`),
  
  // Device control (for web dashboard to control IoT devices)
  control: (deviceId, action, data = {}) => 
    api.post(`/device/${deviceId}/control`, { action, ...data }),
  getStatus: (deviceId) => api.get(`/device/${deviceId}/status`),
  
  // Broadcast control (send commands to all online devices)
  broadcastControl: (action, data = {}) => 
    api.post('/device/broadcast/control', { action, ...data }),
  
  // Convenience methods for device control
  play: (deviceId, musicId = null, playlistId = null) => 
    api.post(`/device/${deviceId}/control`, { 
      action: 'play', 
      music_id: musicId, 
      playlist_id: playlistId 
    }),
  pause: (deviceId) => 
    api.post(`/device/${deviceId}/control`, { action: 'pause' }),
  stop: (deviceId) => 
    api.post(`/device/${deviceId}/control`, { action: 'stop' }),
  setVolume: (deviceId, volume) => 
    api.post(`/device/${deviceId}/control`, { action: 'volume', volume }),
  setDeviceVolume: (deviceId, deviceVolume) => 
    api.post(`/device/${deviceId}/control`, { action: 'device_volume', device_volume: deviceVolume }),
  nextTrack: (deviceId) => 
    api.post(`/device/${deviceId}/control`, { action: 'next' }),
  previousTrack: (deviceId) => 
    api.post(`/device/${deviceId}/control`, { action: 'previous' }),
  
  // Broadcast convenience methods
  broadcastPlay: (musicId = null, playlistId = null) => 
    api.post('/device/broadcast/control', { 
      action: 'play', 
      music_id: musicId, 
      playlist_id: playlistId 
    }),
  broadcastPause: () => 
    api.post('/device/broadcast/control', { action: 'pause' }),
  broadcastStop: () => 
    api.post('/device/broadcast/control', { action: 'stop' }),
  broadcastSetVolume: (volume) => 
    api.post('/device/broadcast/control', { action: 'volume', volume }),
  broadcastNextTrack: () => 
    api.post('/device/broadcast/control', { action: 'next' }),
  broadcastPreviousTrack: () => 
    api.post('/device/broadcast/control', { action: 'previous' }),
};

// Health check API
export const healthAPI = {
  check: () => api.get('/health/'),

};

// Utility functions for debugging token issues
export const tokenUtils = {
  // Get current tokens
  getTokens: () => ({
    accessToken: localStorage.getItem('token'),
    refreshToken: localStorage.getItem('refreshToken'),
    user: localStorage.getItem('user'),
  }),
  
  // Clear all tokens
  clearTokens: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },
  

  
  // Check if token is expired (basic check)
  isTokenExpired: (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  },
  
  // Get token expiration info
  getTokenInfo: (token) => {
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        sub: payload.sub,
        exp: payload.exp,
        iat: payload.iat,
        expiresAt: new Date(payload.exp * 1000),
        issuedAt: new Date(payload.iat * 1000),
        isExpired: payload.exp < (Date.now() / 1000)
      };
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }
};

export default api;
